import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileUpload } from '@fortawesome/free-solid-svg-icons';
import Modal from '../../common/Modal';
import { TaskApi } from './TaskApi';
import toast from 'react-hot-toast';
import { on } from 'events';

interface BulkUploadProps {
  projectId: string;
  projectName: string;
  status: string;
  description: string;
  tenantId: string;
  tenantName: string;
  userId: string;
  username: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
    onError?: (error: string) => void;
}

const BulkTaskUploadModal: React.FC<BulkUploadProps> = ({
  projectId,
  projectName,
  status,
  description,
  tenantId,
  tenantName,
  userId,
  username,
  isOpen,
  onClose,
  onSuccess,
  onError = (error: string) => toast.error(error),
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error('Only .xls or .xlsx files are allowed');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleReset = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!file) return toast.error('Please select an Excel file to upload');

    setLoading(true);
    try {
      await TaskApi.bulkUpload(
        file,
        projectId,
        projectName,
        tenantId,
        tenantName,
        userId,
        username
      );
      toast.success('Tasks uploaded successfully');
      handleReset();         // Clear input
      onSuccess();           // Refresh task list
      onClose();             // Close modal
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        'Upload failed. Please try again.';
        onError(errorMsg); // Call onError callback
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Bulk Task Upload">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium">Project ID</label>
          <div className="bg-gray-100 p-2 rounded">{projectId}</div>
        </div>
        <div>
          <label className="block text-sm font-medium">Project Name</label>
          <div className="bg-gray-100 p-2 rounded">{projectName}</div>
        </div>
        <div>
          <label className="block text-sm font-medium">Status</label>
          <div className="bg-gray-100 p-2 rounded">{status}</div>
        </div>
        <div>
          <label className="block text-sm font-medium">Description</label>
          <div className="bg-gray-100 p-2 rounded">{description}</div>
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="excel-upload" className="text-sm font-medium">
            Upload Excel
          </label>
          <input
            id="excel-upload"
            type="file"
            accept=".xls,.xlsx"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="block text-sm text-gray-600"
          />
          {file && (
            <span className="text-sm text-green-600 truncate">{file.name}</span>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <button
            type="button"
            className="bg-gray-300 text-gray-800 px-4 py-1 rounded hover:bg-gray-400"
            onClick={handleReset}
            disabled={loading}
          >
            Reset
          </button>
          <button
            type="button"
            className="bg-red-500 text-white px-4 py-1 rounded hover:bg-red-600"
            onClick={() => {
              handleReset();
              onClose();
            }}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bg-blue-600 text-white px-4 py-1 rounded hover:bg-blue-700"
            onClick={handleUpload}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faFileUpload} className="mr-1" />
            {loading ? 'Uploading...' : 'Bulk Upload'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkTaskUploadModal;